import React, { useEffect, useState, useRef } from 'react';
import { getScopeRole } from '@/services/current-context';
import { useDispatch } from 'react-redux';
import CheckboxTree from 'react-checkbox-tree';
import { ChevronDown, ChevronRight, ChevronUp, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useOnClickOutside } from '@/components/ui/hooks/use-on-click-outside';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import type { EntityTreeNode } from '@/types/order.types';
import { switchEntity } from '@/services/entity-scope';
import { isSessionEnding } from '@/services/switch-errors';
import { isSelectionKnownUnreachable } from '@/services/entity-scope-read';
import { toRoleSuffix } from '@/services/entity-resolution';
import {
  useParentEntityId,
  useSelectedEntityId,
  useSelectedOrgName,
} from '@/hooks/useEntityScope';

// Root sizing per `layout`. The trigger and the dropdown panel are both
// `w-full` of the root, so they agree by construction.
// The header root is a flex item in the app-header's left group: 25rem when
// there's room, giving way down to 12rem when the header is tight (DV-3474)
// so the controls to its right never wrap or run off the window.
const ROOT_LAYOUT_CLASSES = {
  header: 'ml-7 w-[25rem] min-w-[12rem] shrink',
  block: ''
} as const;

// Internal shape after `addCustomLabel` decorates an EntityTreeNode with the
// JSX label react-checkbox-tree renders. Children become optional because
// addCustomLabel drops the `children` field on leaves.
type RenderedTreeNode = Omit<EntityTreeNode, 'label' | 'children'> & {
  label: string | React.ReactElement;
  children?: RenderedTreeNode[];
};

export type TreeSelectConfiguration = {
  nodes: EntityTreeNode[];
  md: string;
  controlId: string;
  ariaDescribedBy: string;
  name: string;
  placeholder?: string;
  info?: string;
  value?: string;
  onChange?: (value: string) => void;
  label?: React.ReactNode;
  isValid?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  successMessage?: string;
  type?: string;
  disabled?: boolean;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Called after a switch completes. The document no longer reloads (BC-1),
   * so a container that wraps this control — the mobile nav drawer — has to
   * close itself.
   */
  onSwitched?: () => void;
  /**
   * How the control sizes itself within its container.
   *
   * - `header` (default): the desktop app-header treatment — a 25rem
   *   control offset from the logo that shrinks (to 12rem at most) when the
   *   header is tight. These are app-header layout facts, not properties
   *   of the control itself.
   * - `block`: fills its container with no offset. Use anywhere the parent
   *   owns the width (e.g. the mobile nav drawer), where the header's
   *   width and left margin render the control visibly off-centre.
   */
  layout?: 'header' | 'block';
};

const TreeSelect = ({
  nodes,
  controlId,
  type,
  ariaDescribedBy,
  placeholder,
  name,
  setIsLoading,
  onSwitched,
  layout = 'header'
}: TreeSelectConfiguration) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [checked, setChecked] = useState<string[]>([]);
  const [filterText, setFilterText] = useState('');
  const [filteredNodes, setFilteredNodes] = useState<RenderedTreeNode[]>([]);
  const [dropDown, setDropDown] = useState(false);
  const [rawNodes, setRawNodes] = useState<EntityTreeNode[]>([]);

  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const role = getScopeRole();
  const noneDisplay: string = 'none-display';

  const filteredExpandableNodes: string[] = [] as string[];

  // The slice is the authority for scope (DV-3013 spec §2.1 class A).
  const selectedOrgName = useSelectedOrgName() ?? '';
  const selectedEntityIdFromScope = useSelectedEntityId() ?? '';
  const parentEntityIdFromScope = useParentEntityId();

  // Local state exists only for the optimistic set-on-click / revert-on-failure
  // (PR #1131 review, finding 3); it is seeded and re-synced from the slice.
  const [selectedEntityName, setSelectedEntityName] = useState<string>(selectedOrgName);
  const [selectedEntityId, setSelectedEntityId] = useState<string>(selectedEntityIdFromScope);

  // Re-syncing here is what makes a switch made ELSEWHERE (another surface, or
  // another tab in PR 4) show up in the header without a document reload.
  useEffect(() => {
    setSelectedEntityName(selectedOrgName);
    setSelectedEntityId(selectedEntityIdFromScope);
  }, [selectedOrgName, selectedEntityIdFromScope]);
  // Live view of the selection for the node click handlers: those closures
  // are built by addCustomLabel and can lag several renders behind state.
  const selectionRef = useRef({
    entityName: selectedEntityName,
    entityId: selectedEntityId
  });
  selectionRef.current = {
    entityName: selectedEntityName,
    entityId: selectedEntityId
  };

  useEffect(() => {
    if (nodes && nodes.length > 0) {
      setRawNodes(nodes);

      const defaultEntityId = selectedEntityIdFromScope;
      let initialExpanded: string[] = [];

      if (defaultEntityId) {
        const path = findPathToNode(nodes, defaultEntityId);
        if (path) {
          initialExpanded = path;
          setExpanded(path);
          const entityName = findEntityNameById(nodes, defaultEntityId);
          setSelectedEntityName(entityName);
          setSelectedEntityId(defaultEntityId);
        }
      } else {
        initialExpanded = [nodes[0].value];
        setExpanded(initialExpanded);
      }

      const transformedNodes = addCustomLabel(nodes, initialExpanded);
      setFilteredNodes(transformedNodes);
    }
    // `selectedEntityIdFromScope` is a dependency because this effect derives
    // the expansion path from it. A PARENT switch refetches `nodes`, so this
    // re-ran anyway; a SUB-ENTITY switch keeps the same parent and the same
    // `nodes`, and the expansion would have stayed on the previous entity.
    // Invisible while a switch reloads the document; not once PR 3 removes
    // that (PR #1146 review, finding 5).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, selectedEntityIdFromScope]);

  useEffect(() => {
    if (dropDown) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    } else {
      setFilterText('');
      if (nodes && nodes.length > 0) {
        setFilteredNodes(addCustomLabel(nodes));
      }
    }
  }, [dropDown]);

  useOnClickOutside(dropdownContainerRef, () => setDropDown(false));

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDropDown(false);
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, []);

  const getTotalNodes = (nodes: EntityTreeNode[]): number =>
    nodes.reduce(
      (count, node) =>
        count + 1 + (node.children ? getTotalNodes(node.children) : 0),
      0
    );

  const totalNodeCount = getTotalNodes(nodes);
  const isDisabled = totalNodeCount <= 1;

  const onCheck = (checkedNodes: string[]) => {
    setChecked(checkedNodes);
  };

  const onExpand = (expandedNodes: string[]) => {
    setExpanded(expandedNodes);
  };

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setFilterText(text);
    filterTree(text);
  };

  const filterTree = (text: string) => {
    if (!text) {
      const path = findPathToNode(nodes, selectedEntityId) || [];
      setExpanded(path);
      setFilteredNodes(addCustomLabel(nodes, path));
      return;
    }
    const filteredNodeList = JSON.parse(
      JSON.stringify(nodes),
    ) as EntityTreeNode[];
    const filteredChildren = filterNodes(
      filteredNodeList[0].children ?? [],
      text,
    );
    filteredExpandableNodes.length = 0;
    setExpanded([]);
    filteredNodeList[0].children = filteredChildren;
    filterOutEmptyChildren(filteredNodeList);
    setFilterResultExpanded(filteredNodeList);
    setFilteredNodes(addCustomLabel(filteredNodeList, []));

    setExpanded(filteredExpandableNodes);
  };

  const filterNodes = (nodes: EntityTreeNode[], text: string) => {
    for (const node of nodes) {
      if (!node.label.toLowerCase().includes(text.toLowerCase())) {
        const nodeChildren = node.children ?? [];
        const leafNode = processNodes(node, text, nodeChildren);
        if (nodeChildren.length === leafNode) {
          for (const index in nodes) {
            if (nodes[index].value === node.value) {
              nodes[index].className = noneDisplay;
              break;
            }
          }
        }
      }
    }
    return nodes;
  };

  const processNodes = (
    node: EntityTreeNode,
    text: string,
    nodeChildren: EntityTreeNode[],
  ): number => {
    let leafNode = 0;
    for (const nodeChild of node.children ?? []) {
      if (!nodeChild.label.toLowerCase().includes(text.toLowerCase())) {
        for (const index in nodeChildren) {
          if (nodeChildren[index].value === nodeChild.value) {
            nodeChildren[index].className = noneDisplay;
            leafNode++;
            break;
          }
        }
      }
    }
    return leafNode;
  };

  const filterOutEmptyChildren = (filteredNodeList: EntityTreeNode[]) => {
    filteredNodeList?.forEach((node) => {
      if (node.children?.length === 0) {
        delete node.children;
      } else if (node.children) {
        filterOutEmptyChildren(node.children);
      }
    });
  };

  const setFilterResultExpanded = (filteredNodeList: EntityTreeNode[]) => {
    filteredNodeList.forEach((node) => {
      filteredExpandableNodes.push(node.value);
      if (node.children?.length) {
        setFilterResultExpanded(node.children);
      }
    });
  };

  const findPathToNode = (
    nodes: EntityTreeNode[],
    targetValue: string,
    path: string[] = []
  ): string[] | null => {
    for (const node of nodes) {
      const currentPath = [...path, node.value];

      if (node.value === targetValue) {
        return currentPath;
      }

      if (node.children && node.children.length > 0) {
        const result = findPathToNode(node.children, targetValue, currentPath);
        if (result) return result;
      }
    }
    return null;
  };

  const findEntityNameById = (
    nodes: EntityTreeNode[],
    targetValue: string,
  ): string => {
    for (const node of nodes) {
      if (node.value === targetValue) {
        return node.label;
      }
      if (node.children) {
        const result = findEntityNameById(node.children, targetValue);
        if (result) return result;
      }
    }
    return '';
  };

  const performSwitch = async (
    entityName: string,
    entityId: string,
    previous: { entityName: string; entityId: string }
  ) => {
    setIsLoading?.(true);
    try {
      // Sub-entity switches stay anchored to the current top-level org — the
      // tree only ever shows the active parent's hierarchy, and
      // useEntityTreeNodes / usePrepaySaveGate key off that anchor.
      await switchEntity(
        {
          entityId,
          entityName,
          role: toRoleSuffix(role),
          parentEntityId: parentEntityIdFromScope ?? entityId
        },
        'stay'
      );
      // Nothing reloads any more, so this surface owns the teardown the reload
      // used to do for it: close the dropdown, drop the overlay (which remounts
      // <Outlet/> under the new scope — see app-shell), and let the container
      // close itself. `onSwitched` goes LAST because it is the mobile drawer's
      // close, which can unmount this component — leaving the overlay to a
      // setter called afterwards would work only because that setter belongs to
      // the parent (PR #1150 review, finding 7).
      setDropDown(false);
      setIsLoading?.(false);
      onSwitched?.();
    } catch (error) {
      // The switch ended the session and its navigation is pending: no revert,
      // no alert over a logout (DV-3421 review round 5, item 4).
      if (isSessionEnding(error)) return;
      // Revert the optimistic selection: the scope did not move, and leaving
      // the target selected would make a retry click short-circuit on the
      // same-name guard in onNodeClick (PR #1131 review, finding 3).
      setSelectedEntityName(previous.entityName);
      setSelectedEntityId(previous.entityId);
      setIsLoading?.(false);
      dispatch(
        showAlert({
          type: Variants.ERROR,
          message: 'Something went wrong, Please contact admin'
        })
      );
    }
  };

  const onNodeClick = (entityName: string, entityId: string) => {
    const previous = { ...selectionRef.current };
    // Re-choosing the account already shown is a no-op — unless this tab's
    // token is known to no longer reach it, when it is the way back (DV-3421).
    if (entityName === previous.entityName && !isSelectionKnownUnreachable()) return false;
    setSelectedEntityName(entityName);
    setSelectedEntityId(entityId);

    const path = findPathToNode(rawNodes, entityId);
    if (path) {
      setExpanded(path);
    }

    performSwitch(entityName, entityId, previous);
  };

  const addCustomLabel = (
    nodes: Array<EntityTreeNode | RenderedTreeNode>,
    expandedNodes: string[] = expanded
  ): RenderedTreeNode[] => {
    const sortedNodes = [...nodes].sort((a, b) => {
      const labelA = (
        typeof a.label === 'string' ? a.label : a.plainLabel || ''
      ).toLowerCase();
      const labelB = (
        typeof b.label === 'string' ? b.label : b.plainLabel || ''
      ).toLowerCase();
      return labelA.localeCompare(labelB);
    });

    return sortedNodes.map((node) => {
      const originalLabel =
        typeof node.label === 'string' ? node.label : node.plainLabel || '';

      const isLeafNode = !node.children || node.children.length === 0;

      const isSelected = node.value === selectedEntityId;
      const isExpanded = expandedNodes.includes(node.value);

      let className = '';
      if (node.className === noneDisplay) {
        className = noneDisplay;
      } else if (isSelected) {
        className = 'selected-entity';
      } else if (isExpanded) {
        className = 'expanded-entity';
      }

      return {
        ...node,
        plainLabel: originalLabel,
        className,
        showCheckbox: false,
        label: (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNodeClick(originalLabel, node.value);
              setDropDown(false);
            }}
            className={cn(
              'border-none py-1 px-2 m-0 text-left bg-transparent text-inherit',
              'w-full overflow-hidden text-ellipsis whitespace-nowrap block',
              'rounded-sm hover:bg-muted',
              className === 'selected-entity' &&
                'bg-primary/10 text-primary rounded-sm',
              className === noneDisplay && 'hidden'
            )}
          >
            {originalLabel}
          </button>
        ),
        children: !isLeafNode
          ? addCustomLabel(node.children ?? [], expandedNodes)
          : undefined
      };
    });
  };

  return (
    <>
    <style>{`
      .entity-tree .react-checkbox-tree { padding: 8px; }
      .entity-tree .rct-text { display: flex; align-items: center; gap: 2px; width: 100%; }
      .entity-tree .rct-bare-label { flex: 1; min-width: 0; }
      .entity-tree .rct-collapse { background: transparent; border: none; cursor: pointer; padding: 2px; border-radius: 4px; flex-shrink: 0; }
      .entity-tree .rct-collapse:hover { background: var(--color-accent); }
      .entity-tree .rct-title { display: block; width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .entity-tree .rct-node-leaf { margin-left: 26px; }
      .entity-tree ol { list-style: none; padding-left: 16px; }
      .entity-tree ol:first-child { padding-left: 0; }
      .entity-tree li { margin: 2px 0; }
      .entity-tree .none-display { display: none; }
    `}</style>
    <div
      className={cn(
        'entity-tree relative flex justify-center items-center',
        ROOT_LAYOUT_CLASSES[layout]
      )}
      ref={dropdownContainerRef}
    >
      <div className="w-full">
        <div
          className={cn(
            'flex items-stretch',
            !isDisabled && 'group'
          )}
        >
          <div
            className="relative flex-1"
            title={selectedEntityName || placeholder}
          >
            <input
              type={type}
              className={cn(
                // h-full, not a fixed height: the chevron segment keeps
                // Button's 44px tap floor below lg, and the field must match
                // it rather than leave a step at the bottom edge (DV-3474).
                'w-full h-full min-h-10 px-3 py-2 text-sm',
                'border border-input border-r-0',
                'rounded-l-md rounded-r-none',
                'cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap',
                'focus:outline-none focus:ring-0 focus:border-input',
                isDisabled
                  ? 'cursor-default text-muted-foreground bg-muted'
                  : 'text-foreground bg-background group-hover:bg-muted'
              )}
              data-testid={controlId + '_id'}
              placeholder={placeholder}
              aria-describedby={ariaDescribedBy}
              name={name}
              value={selectedEntityName}
              onClick={() => {
                if (!isDisabled) setDropDown(!dropDown);
              }}
              readOnly
              disabled={isDisabled}
            />
          </div>
          <div
            className={cn(
              'flex items-center border border-input border-l-0',
              'rounded-r-md px-1',
              isDisabled
                ? 'bg-muted'
                : 'bg-transparent group-hover:bg-muted'
            )}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                if (!isDisabled) setDropDown(!dropDown);
              }}
              disabled={isDisabled}
            >
              {dropDown ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
      <div
        className={cn(
          'absolute top-[102%] left-0 z-50 w-full',
          'min-h-[200px] max-h-[400px]',
          'bg-background shadow-popover overflow-auto rounded-lg border border-border',
          !dropDown && 'hidden'
        )}
      >
        <div className="sticky top-0 bg-background z-10 p-3 pb-2 border-b border-border">
          <div className="relative">
            {/* Self-centering (not a fixed top offset): the input below is
                h-8 on desktop but Input's base max-lg:h-11 tap-target floor
                wins below lg (twMerge keeps differently-prefixed h-*
                utilities side by side), so the icon must center at either
                height. */}
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              className="pl-8 h-8 text-sm"
              type="text"
              value={filterText}
              onChange={onFilterChange}
              data-testid="tree-search-input"
              placeholder="Search..."
            />
          </div>
        </div>
        <CheckboxTree
          checked={checked}
          expanded={expanded}
          nodes={filteredNodes}
          onCheck={onCheck}
          onExpand={onExpand}
          showNodeIcon={false}
          icons={{
            expandClose: <ChevronRight className="h-4 w-4" />,
            expandOpen: <ChevronDown className="h-4 w-4" />,
          }}
        />
      </div>
    </div>
    </>
  );
};

export { TreeSelect };
export type { TreeSelectConfiguration as TreeSelectConfig };
